import { useAtom } from 'jotai';
import { useEffect } from 'react';

import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import useGetExistingIncomeSplitConfigs from '../../components/income-split/hooks/useGetIncomeSplitConfigs';
import { incomeSplitAtom } from '../../state/PaycheckRoutingAtoms';
import { isAccountValidAsIncomeSplitSource } from '../../utils/IncomeSplitUtils';
import useGetAllBankAccountDetailsFromGlobalState from '../useGetAllBankAccountDetailsFromGlobalState';

const useGetIncomeSplitFromGlobalState = () => {
    const user = useAuthedUser();

    const [incomeSplit, setIncomeSplit] = useAtom(incomeSplitAtom);

    const { bankAccountDetails, isLoading: isLoadingBankAccountDetails } =
        useGetAllBankAccountDetailsFromGlobalState();

    const { incomeSplitConfigs, isLoading: isLoadingIncomeSplitConfigs } =
        useGetExistingIncomeSplitConfigs();

    const isLoading =
        isLoadingIncomeSplitConfigs || isLoadingBankAccountDetails;

    useEffect(() => {
        if (!isLoading) {
            // Check if incomeSplit is null or an empty object (which shouldn't happen but can)
            const hasNoIncomeSplit =
                !incomeSplit || Object.keys(incomeSplit).length === 0;

            if (
                hasNoIncomeSplit &&
                incomeSplitConfigs?.[incomeSplitConfigs.length - 1]
            ) {
                const latestConfig =
                    incomeSplitConfigs[incomeSplitConfigs.length - 1];

                if (latestConfig) setIncomeSplit(latestConfig);
            } else if (
                hasNoIncomeSplit &&
                !(incomeSplitConfigs ?? []).length &&
                bankAccountDetails.length > 0
            ) {
                // we've determined there's no existing config, create a new one
                const newConfig = {
                    onlyExecuteOnPaychecks: false,
                    accounts: bankAccountDetails
                        .filter((account) => {
                            return isAccountValidAsIncomeSplitSource(
                                user,
                                account,
                            );
                        })
                        .map((account) => {
                            return {
                                accountId: account.bankAccountId,
                            };
                        }),
                    ruleSets: [],
                    isNew: true,
                };
                setIncomeSplit(newConfig);
            } else if (
                !hasNoIncomeSplit &&
                incomeSplitConfigs?.[incomeSplitConfigs.length - 1]
            ) {
                // incomeSplit exists but might be stale - update it if configs have changed
                const latestConfig =
                    incomeSplitConfigs[incomeSplitConfigs.length - 1];
                if (latestConfig && latestConfig.id !== incomeSplit?.id) {
                    setIncomeSplit(latestConfig);
                }
            }
        }
    }, [
        JSON.stringify(incomeSplitConfigs),
        isLoading,
        incomeSplit,
        bankAccountDetails,
        user,
    ]);

    return { incomeSplit, isLoading };
};

export default useGetIncomeSplitFromGlobalState;
